import Cookies from "js-cookie";
import type {
  User,
  UserFavouriteMenu,
} from "@/app/services/generated/userService";
import api from "@/app/api";
import type {
  Tenant,
  TenantGroup,
} from "@/app/services/generated/tenantService";
import Guid from "@/app/types/common/guid";
import { EnumUserTenantGroupLevel } from "@/app/services/generated/enums/enumUserTenantGroupLevel.generated";
import type { BillingStatus } from "@/app/services/generated/billingplanService";
import { billingPlanService } from "@/app/services/generated/billingplanService";
import type { DeepReadonly } from "vue";
import { computed, reactive, watch } from "vue";
import { getById } from "@/app/apiutils";
import { BrewManError } from "@/app/errors";
import { displayUserInitials } from "@/components/mixins/DisplayHelpers";
import { authenticationService } from "@/app/services/generated/authenticationService";
import { userSessionService } from "@/app/services/generated/usersessionService";
import { userService } from "@/app/services/extensions/userServiceExtended";
import { tenantService } from "@/app/services/extensions/tenantServiceExtended";
import environment from "@/environment";
import { loadForTenant } from "@/components/composables/localisation";
import {
  signOut as signOutOfGip,
  currentGipUser,
} from "@/components/pages/account/composables/authentication";
import { parseLangLocaleOrFallback } from "@/locales/langLocales";
import type { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import messages from "@/plugins/i18n";
import eventLogger from "@/app/eventlogging/eventLogger";
import { BrowserIdentityManager } from "@/store/modules/browserIdentityManager";
import eventShipper from "@/app/eventlogging/eventShipper";
import { salesUserSettingsService } from "@/app/services/generated/salesusersettingsService";
import { setSalesCodeRestriction } from "@/store/modules/userSalesCodeStore";

const defaultCountry = "United Kingdom";
const countryKey = "brewcountry";

interface AvailableTenantGroup {
  name: string;
  id: Guid;
}

interface UserStoreState {
  tenantGroup: TenantGroup | null;
  user: User | null;
  tenant: Tenant | null;
  allAvailableTenants: readonly Tenant[];
  allAvailableTenantGroups: readonly AvailableTenantGroup[] | null;
  hasLicense: boolean;
  billingStatus: BillingStatus | null;
  isFirstLogin: boolean;
  country: string;
  userLanguageLocale: string;
  userLanguage: string;
  userPermissions: Readonly<EnumUserPermission[]>;
  favourites: readonly UserFavouriteMenu[];
}

const state = reactive<UserStoreState>({
  tenantGroup: null,
  user: null,
  tenant: null,
  allAvailableTenants: [],
  allAvailableTenantGroups: null,
  hasLicense: true,
  billingStatus: null,
  isFirstLogin: false,
  country: "United Kingdom",
  userLanguageLocale: "en",
  userLanguage: "en",
  userPermissions: [],
  favourites: [],
}) as UserStoreState;

function getSessionCookie() {
  return Cookies.get("bmsession") ?? null;
}

function setSessionCookie(sessionToken: string) {
  Cookies.set("bmsession", sessionToken, {
    secure: true,
    sameSite: "strict",
  });
}

function clearSessionCookie(sessionToken: string) {
  if (getSessionCookie() === sessionToken) {
    Cookies.remove("bmsession", {
      secure: true,
      sameSite: "strict",
    });
  }
}

function logSessionState(reason: string) {
  eventLogger.logEvent("SESSION - " + reason, {
    tenantId: state.tenant?.id.toString() ?? null,
    tenantGroupId: state.tenant?.tenantGroupId?.toString() ?? null,
    userId: state.user?.id.toString() ?? null,
    sessionToken: api.sessionToken,
  });
}

async function clearState() {
  logSessionState("clearState - state before clearing");
  await eventShipper.flush(2000);
  state.hasLicense = true; // default to licensed to avoid session kicker during login
  state.user = null;
  state.tenant = null;
  state.tenantGroup = null;
  state.isFirstLogin = false;
  state.billingStatus = null;

  api.sessionToken = null;
  api.tenantId = Guid.createEmpty();
  api.tenantGroupId = Guid.createEmpty();
}

async function setLanguageLocale(languageLocale: string): Promise<void> {
  if (state.user) {
    await userService.updateSelectedLanguageLocale(
      api.tenantGroupId,
      languageLocale
    );
    await refreshStore();
  }
}

async function setFavourites(
  favourites: readonly UserFavouriteMenu[]
): Promise<void> {
  if (state.user) {
    await userService.saveUserFavouriteMenus(favourites);
    await refreshStore();
  }
}

function setCountry(country: string): void {
  window.localStorage.setItem(countryKey, country);
  state.country = country;
}

const isAuthenticatedWithSelectedTenant = computed(
  () =>
    state.user !== null && state.tenantGroup != null && state.tenant !== null
);

/**
 * Loads default state from storage - call this on store creation.
 */
async function load(): Promise<void> {
  const sessionToken = getSessionCookie();

  if (sessionToken) {
    logSessionState("restoring session token " + sessionToken);

    try {
      await signInWithSessionToken(sessionToken);

      logSessionState("restored session token");
    } catch (_) {
      // Load is best-effort at re-signing the user in. If we propagate exceptions here they will prevent the main
      // application from loading.
    }
  } else {
    logSessionState("userStore.load - no session cookie to restore");
  }

  try {
    setCountry(window.localStorage.getItem(countryKey) || defaultCountry);
  } catch (e) {
    setCountry(defaultCountry);
  }

  await eventShipper.flush(2000);
}

async function signInWithGip(signInCredentials: {
  tenantGroupId: Guid;
  gipId: string;
}) {
  const browserInstanceId =
    BrowserIdentityManager.getInstance().getBrowserInstanceId();

  const sessionToken = await userService.loginWithGipIdToken(
    signInCredentials.tenantGroupId,
    signInCredentials.gipId,
    browserInstanceId
  );

  logSessionState("signInWithGip token " + sessionToken);

  await signInWithSessionToken(sessionToken);
}

async function supportGoogleSignIn(googleSignInCredentials: {
  tenantGroupName: string;
  gipId: string;
}) {
  const browserInstanceId =
    BrowserIdentityManager.getInstance().getBrowserInstanceId();

  const sessionToken = await userService.loginSupportUserWithGipIdToken(
    googleSignInCredentials.tenantGroupName,
    googleSignInCredentials.gipId,
    browserInstanceId
  );
  await signInWithSessionToken(sessionToken);
}

async function signOut(
  reason: string,
  fromGip: boolean = false
): Promise<void> {
  // any problems or if no auth token - then clear everything
  try {
    if (api.sessionToken && state.tenant) {
      if (fromGip) {
        await signOutOfGip();
      }
      await authenticationService.logOutOfSession(state.tenant.tenantGroupId);

      // Our session token is no longer valid, so we want to remove it from the cookie
      // but only if it is the latest one. If someone (eg another tab) has stored a later
      // cookie then we should allow that to continue.
      clearSessionCookie(api.sessionToken);

      logSessionState("signOut " + reason);
      await eventShipper.flush(2000);
    }
  } catch (e) {
    // Found out the hard way that an exception here leaves the client code bricked
  }

  await clearState();
}

async function performLicenseCheck(): Promise<boolean> {
  let hasLicense = true;
  if (
    isAuthenticatedWithSelectedTenant.value &&
    state.user &&
    !state.user.readOnly.isSupportUser &&
    !state.user.readOnly.isSystemUser
  ) {
    hasLicense = await userSessionService.tryGetLicenseForCurrentSession();
  }

  state.hasLicense = hasLicense;
  return hasLicense;
}

/**
 * Note. This method is susceptible to creating subtle race conditions. As this method sets properties between async
 * calls - any computed props from this store may start changing state before the method completes. It is important
 * clients do not start using api calls that require tenant id before refresh is called.
 */
async function refreshBillingStatus(): Promise<BillingStatus | null> {
  let billingStatus: BillingStatus | null = null;

  if (!state.user?.readOnly.isSystemUser) {
    billingStatus = await billingPlanService.getBillingStatus(
      api.tenantGroupId
    );
  }

  state.billingStatus = billingStatus;
  return billingStatus;
}

async function signInWithSessionToken(sessionToken: string): Promise<void> {
  await clearState();

  setSessionCookie(sessionToken);

  // Set session token on api
  api.sessionToken = sessionToken;

  logSessionState("signing in with session token");

  try {
    await refreshStore();

    logSessionState("signed in with session token");
    await eventShipper.flush(2000);
  } catch (e) {
    // If any problems occur during log in (e.g. unauthenticated) then clear down state.
    await clearState();

    if (!navigator.onLine) {
      return;
    } else {
      clearSessionCookie(sessionToken);
      throw e;
    }
  }
}

async function switchTenantAndRefresh(tenantId: Guid) {
  if (
    state.tenant &&
    state.tenantGroup &&
    state.user &&
    state.user.readOnly.selectedTenant !== state.tenant.id
  ) {
    await userService.updateSelectedTenantId(
      state.tenant.tenantGroupId,
      state.user.id,
      tenantId
    );
    window.location.href = "/";
  }
}

async function switchTenantGroupAndRefresh(tenantGroupId: Guid) {
  const oldSessionToken = api.sessionToken;
  if (oldSessionToken) {
    const newSession =
      await userSessionService.swapUserSessionForAnotherTenantGroup(
        tenantGroupId
      );

    // Discard the previous session token
    clearSessionCookie(oldSessionToken);

    if (newSession) {
      // Store the new session token (for the new tenant group)
      setSessionCookie(newSession.token);
    }
  }
  window.location.href = "/";
}

async function refreshStore() {
  // Find the current user details
  const userContext = await userService.getCurrentUserContext();
  const [allAvailableTenants, tenantGroup] = await Promise.all([
    tenantService.getTenantsForTenantGroup(userContext.tenantGroupId),
    tenantService.getTenantGroup(userContext.tenantGroupId),
  ]);

  state.allAvailableTenants = allAvailableTenants;
  state.tenantGroup = tenantGroup;

  // Resolve the current tenant with following rules:
  // 1) requested, 2) previous current (a refresh), 3) saved user selected 4) first demo tenant 5) first tenant
  state.tenant =
    (state.tenant?.id ? getById(allAvailableTenants, state.tenant.id) : null) ??
    (userContext.user.readOnly.selectedTenant
      ? getById(allAvailableTenants, userContext.user.readOnly.selectedTenant)
      : null) ??
    allAvailableTenants.find((tenant) => tenant.isDemoData) ??
    allAvailableTenants[0];

  if (state.tenant == null) {
    throw new BrewManError(
      "Could not resolve tenant for tenant group: " + state.tenantGroup.name
    );
  }

  // Update api for tenant persistence
  api.tenantId = state.tenant.id;
  api.tenantGroupId = state.tenant.tenantGroupId;
  tenantService.tenantChanged(state.tenant as Tenant);

  await loadForTenant(state.tenant);

  // Write the new selected tenant back to the user if different
  if (userContext.user.readOnly.selectedTenant !== state.tenant.id) {
    try {
      await userService.updateSelectedTenantId(
        state.tenant.tenantGroupId,
        userContext.user.id,
        state.tenant.id
      );
    } catch (_) {
      // Intentionally empty
    }
  }

  // Load all the settings data
  state.userPermissions = await userService.getPermissionsForUser(
    api.tenantId,
    userContext.user.id
  );
  setSalesCodeRestriction(
    (
      await salesUserSettingsService.getSalesUserConfiguration(
        userContext.user.id
      )
    ).userSettings.salesCodeId
  );

  state.favourites = await userService.getUserFavouriteMenus();

  // Set default user language
  state.userLanguageLocale = parseLangLocaleOrFallback(
    userContext.user.languageLocale ||
      state.tenant.languageAndCultureConfiguration.cultureCode ||
      "en"
  ).langLocale;
  state.userLanguage = state.userLanguageLocale.split("-")[0] || "en";

  // Once we've loaded everything for the user, set it.
  // The distribution app will watch for this, so make sure we've done everything first.
  // performLicenseCheck relies on the user, so this will need to be done before
  state.user = userContext.user;

  // Actions here can use current state
  await refreshBillingStatus();
  await performLicenseCheck();
}

// get tenant groups for current user
async function refreshAllAvailableTenantGroups() {
  if (currentGipUser.value && currentGipUser.value.emailVerified) {
    const gipIdToken = await currentGipUser.value.getIdToken(true);
    if (gipIdToken) {
      state.allAvailableTenantGroups = (
        await userService.findUsersMatchingGipIdToken(gipIdToken)
      ).map((user) => {
        return {
          name: user.tenantGroupName,
          id: user.user.readOnly.tenantGroupId,
        } as AvailableTenantGroup;
      });
    }
  }
}

watch(currentGipUser, refreshAllAvailableTenantGroups, { immediate: true });

// This is outside of the store itself
export function requireUserStoreUser(
  user: DeepReadonly<User> | null
): asserts user is User {
  if (user === null) {
    throw new BrewManError(messages.unknown_user_id);
  }
}

export default {
  // User is sometimes used as a prop with type User | null
  // Only make a shallow readonly user instead of deep readonly
  state: state as DeepReadonly<Omit<UserStoreState, "user">> & {
    readonly user: UserStoreState["user"];
  },
  userInitials: computed(() =>
    state.user ? displayUserInitials(state.user.name) : ""
  ),

  hasAdministrativePrivileges: computed(
    () =>
      (state.user?.userTenantGroupLevel ?? EnumUserTenantGroupLevel.member)
        .key >= EnumUserTenantGroupLevel.administrator.key
  ),

  isAuthenticatedWithSelectedTenant,

  load,
  supportGoogleSignIn,
  signInWithSessionToken,
  signOut,
  refreshStore,
  signInWithGip,

  performLicenseCheck,
  setHasLicense: (hasLicense: boolean) => {
    state.hasLicense = hasLicense;
    logSessionState("setHasLicence " + hasLicense);
    eventShipper.flush();
  },
  setLanguageLocale,
  setFavourites,
  setCountry,
  switchTenantGroupAndRefresh,
  switchTenantAndRefresh,
  refreshAllAvailableTenantGroups,
  isPremierOrSupportUser: computed(
    () =>
      (state.user?.readOnly.isSupportUser ||
        state.user?.email.endsWith(environment.companyDomain)) &&
      state.user?.email !== environment.integrationUserEmail // The avatar dropdown causes problems with tests
  ),
  logSessionState,
};
